import React, { useContext } from 'react';
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from '@apollo/client';
import get from 'lodash/get';
import { Info } from '@phosphor-icons/react';

import Input from '../../../components/Input';
import { SingleCheckbox } from '../../../components/Checkbox';
import { CONFIRM_HUB_COUNTER, ConfirmHubCounterMutation, GET_SUGGESTED_HUB_COUNTER_ID, SuggestedHubCounterIdQuery } from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';
import UserContext from '../../../contexts/UserContext';

const ConfirmHubCounterSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

interface ConfirmHubCounterModalProps {
  isOpen: boolean;
  onClose: () => void;
  dropPointId: string;
  initialHubCounterId?: string;
  refetch: () => Promise<any> | void;
  refetchState?: () => Promise<any> | void;
}

const ConfirmHubCounterModal = ({
  isOpen,
  onClose,
  dropPointId,
  initialHubCounterId,
  refetch,
  refetchState,
}: ConfirmHubCounterModalProps) => {
  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useSpinner();
  const toast = useCustomToast();
  const { data, loading: suggestedLoading } = useQuery<SuggestedHubCounterIdQuery>(GET_SUGGESTED_HUB_COUNTER_ID, {
    variables: { dropPointId },
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
  });
  const [confirmHubCounter] = useMutation<ConfirmHubCounterMutation>(CONFIRM_HUB_COUNTER);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Nur Amazon aktivieren</ModalHeader>
        <Formik
          enableReinitialize
          initialValues={{
            hubCounterId: initialHubCounterId || data?.suggestedHubCounterId || '',
            skipFeedCreation: false,
            comment: '',
          }}
          validationSchema={ConfirmHubCounterSchema}
          onSubmit={async ({ hubCounterId, skipFeedCreation, comment }) => {
            try {
              toggleSpinner(true);
              await confirmHubCounter({
                variables: {
                  dropPointId,
                  hubCounterId: hubCounterId.trim() || undefined,
                  skipFeedCreation: skipFeedCreation || undefined,
                  comment,
                  internalUserId: internalUser.id,
                },
              });
              await Promise.all([Promise.resolve(refetch()), Promise.resolve(refetchState?.())]);
              toast({
                title: 'Erfolg!',
                description: skipFeedCreation
                  ? 'Amazon Hub Counter bestätigt ohne CREATE-Feed. UPS bleibt unverändert.'
                  : 'Amazon Hub Counter wurde bestätigt. UPS bleibt unverändert.',
                status: 'success',
              });
              onClose();
            } catch (error) {
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Fehler beim Bestätigen.'),
                status: 'error',
              });
            } finally {
              toggleSpinner(false);
            }
          }}
        >
          <Form>
            <ModalBody>
              <Text fontSize="sm" mb="3">
                Hier wird der DropPoint als Amazon Hub Counter im DropFriends-System angelegt und aktiviert. Das hat keinen Einfluss auf das
                UPS Access Point Programm.
              </Text>
              <Text fontSize="sm" mb="4">
                Zum Anlegen wird eine Hub Counter ID benötigt. Diese wird als eindeutige Bezeichnung des DropPoints in den Systemen von
                Amazon und DropFriends verwendet. Ohne Eingabe verwendet das Backend automatisch die nächste freie ID.
              </Text>
              <Input name="hubCounterId" label="Hub Counter ID" />
              <Text fontSize="xs" color="gray.500" mt="2" mb="4">
                {suggestedLoading
                  ? 'Vorschlag wird geladen...'
                  : data?.suggestedHubCounterId
                    ? `Backend-Vorschlag: ${data.suggestedHubCounterId}`
                    : 'Es liegt aktuell kein Vorschlag vor.'}
              </Text>
              <Input name="comment" label="Kommentar/Begründung" />
              <SingleCheckbox
                name="skipFeedCreation"
                label={
                  <Flex align="center" gap="2">
                    <Text fontSize="sm">DropPoint-Daten nicht automatisch an Amazon übertragen.</Text>
                    <Tooltip
                      placement="auto-start"
                      label="Wenn Du die Checkbox aktivierst, dann muss die Übertragung der DropPoint-Daten anschließend manuell durch Osamah per API erfolgen. Alternativ kann eine CSV mit den DropPoint-Daten an Amazon gesendet werden."
                      aria-label="Hinweis zur manuellen Übertragung der DropPoint-Daten"
                    >
                      <Box onClick={(event) => event.preventDefault()}>
                        <Info />
                      </Box>
                    </Tooltip>
                  </Flex>
                }
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                Abbrechen
              </Button>
              <Button ml="4" size="sm" colorScheme="brand" type="submit">
                Amazon aktivieren
              </Button>
            </ModalFooter>
          </Form>
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default ConfirmHubCounterModal;
